import type { FilteredBucketValues } from './CubeFiltering.ts'
import type { TaxaAggregationResult, TaxaBucketRow } from './TaxaAggregation.ts'
import { profile } from '../../../../../Repository/Visualization/VizPerf.ts'

export function aggregateTaxaAllBuckets(opts: { filteredBuckets: FilteredBucketValues[]; level: number }): TaxaAggregationResult {
  return profile('cube.aggregate', () => aggregateTaxaAllBucketsImpl(opts), {
    buckets: opts.filteredBuckets.length,
    level: opts.level
  })
}

function aggregateTaxaAllBucketsImpl(opts: { filteredBuckets: FilteredBucketValues[]; level: number }): TaxaAggregationResult {
  const { filteredBuckets, level } = opts

  const taxaSet = new Set<string>()
  const rows: TaxaBucketRow[] = []

  for (const bucket of filteredBuckets) {
    const agg: Record<string, number> = {}

    for (const v of bucket.values) {
      const nameAtLevel = v.taxa[level] ?? '(n/a)'
      agg[nameAtLevel] = (agg[nameAtLevel] ?? 0) + (v.magnitude ?? 0)
    }

    for (const k of Object.keys(agg)) taxaSet.add(k)

    rows.push({
      bucketKey: bucket.bucketKey,
      bucketLabel: bucket.bucketLabel,
      sampleCount: bucket.sampleCount,
      data: agg
    })
  }

  return { taxa: Array.from(taxaSet), rows }
}

export function aggregateTaxaSingleBucket(opts: {
  filteredBuckets: FilteredBucketValues[]
  level: number
  bucketKey: string
}): TaxaAggregationResult {
  const { filteredBuckets, level, bucketKey } = opts

  const bucket = filteredBuckets.find((x) => x.bucketKey === bucketKey) ?? {
    bucketKey,
    bucketLabel: bucketKey,
    values: []
    /* no sampleCount: nothing was found, so the count is unknown rather than zero */
  }

  const agg: Record<string, number> = {}
  for (const v of bucket.values) {
    const nameAtLevel = v.taxa[level] ?? '(n/a)'
    agg[nameAtLevel] = (agg[nameAtLevel] ?? 0) + (v.magnitude ?? 0)
  }

  const taxa = Object.keys(agg)
  return {
    taxa,
    rows: [{ bucketKey: bucket.bucketKey, bucketLabel: bucket.bucketLabel, sampleCount: bucket.sampleCount, data: agg }]
  }
}

/** Label for reads whose entire lineage is unresolved — kept as ONE honest bucket, never dropped. */
export const UNRESOLVED_LABEL = 'Unresolved'

/**
 * Tokens that are NOT a real taxon name (case-insensitive, trimmed). Some OTUs carry a literal "nan"
 * (a pandas NaN→str artifact) or similar placeholders as a lineage entry; these must not be treated as
 * an organism. Shared so every "is this a real taxon?" decision here stays consistent.
 */
const NON_TAXON_TOKENS = new Set(['nan', 'none', 'null', '(n/a)', 'n/a', 'unclassified', 'unknown', ''])

/** True when [name] is a real, resolved taxon (not a NaN-like / placeholder token). */
function isMeaningfulTaxon(name: string | null | undefined): boolean {
  if (name == null) return false
  return !NON_TAXON_TOKENS.has(name.trim().toLowerCase())
}

/**
 * The deepest MEANINGFUL taxon in a compacted lineage: walk UP from the deepest entry, skipping
 * unresolved/placeholder tokens (see [NON_TAXON_TOKENS]). E.g. [...,'Diptera','nan'] → "Diptera"
 * (the nan-tailed reads fold up into Diptera — still a Diptera organism, just unresolved further).
 * Returns [UNRESOLVED_LABEL] when every entry is unresolved/empty.
 */
export function deepestMeaningfulTaxon(taxa: string[]): string {
  for (let i = taxa.length - 1; i >= 0; i--) {
    if (isMeaningfulTaxon(taxa[i])) return taxa[i]!
  }
  return UNRESOLVED_LABEL
}

/**
 * Single-bucket aggregation by the DEEPEST-resolved taxon (the "leaf" organism), NOT by the
 * child-at-a-level like {@link aggregateTaxaSingleBucket}.
 *
 * This is what "most abundant ORGANISMS" means: each cube value is one `(bucket, tax_path)` whose
 * `taxa` ladder has already had blanks/nulls stripped ({@link filterCubeByPathAndGrade}). The leaf is
 * the deepest MEANINGFUL entry (species, or genus/family when species is unresolved — e.g. "Culicidae",
 * "Leiopus"), skipping NaN-like placeholder tokens ("nan"/"unclassified"/…) that some OTUs carry so a
 * "nan" bar can never dominate the ranking; reads for an all-unresolved lineage go to a single
 * [UNRESOLVED_LABEL] bucket rather than being dropped. The caller's `selectedPath`/grade filter is
 * applied UPSTREAM by the shared cube filter. Feed the result to `rankTopTaxa` (sort + top-N).
 *
 * Ranking (sort desc + top-N slice) is intentionally left to `rankTopTaxa` so this stays a plain sum.
 */
export function aggregateTaxaLeafSingleBucket(opts: {
  filteredBuckets: FilteredBucketValues[]
  bucketKey: string
}): TaxaAggregationResult {
  const { filteredBuckets, bucketKey } = opts

  const bucket = filteredBuckets.find((x) => x.bucketKey === bucketKey) ?? {
    bucketKey,
    bucketLabel: bucketKey,
    values: []
    /* no sampleCount: nothing was found, so the count is unknown rather than zero */
  }

  const agg: Record<string, number> = {}
  for (const v of bucket.values) {
    const leaf = deepestMeaningfulTaxon(v.taxa)
    agg[leaf] = (agg[leaf] ?? 0) + (v.magnitude ?? 0)
  }

  const taxa = Object.keys(agg)
  return {
    taxa,
    rows: [{ bucketKey: bucket.bucketKey, bucketLabel: bucket.bucketLabel, sampleCount: bucket.sampleCount, data: agg }]
  }
}

/**
 * Canonical taxonomy-rank NAME → position in the ladder (`tax_0_domain … tax_7_species`, see the
 * DB schema). This is the ONLY named-rank ↔ index mapping in the frontend, so keep it here.
 *
 * CAVEAT (compacted ladder): the cube's `taxa` array is COMPACTED — blank ranks are stripped
 * ({@link filterCubeByPathAndGrade}) — so `taxa[depth]` equals the semantic rank only when the
 * ladder above it is fully populated (e.g. NCBI's "Eukaryota" domain). For a source that leaves
 * an upper rank blank (BOLD leaves `domain` empty) the positions shift up by one. This is the same
 * depth-based assumption the rest of the viz already makes (zoom is `selectedPath.length`), so a
 * named `rankLevel` inherits it rather than inventing a second, conflicting model.
 */
export const RANK_NAME_TO_DEPTH: Record<string, number> = {
  domain: 0,
  kingdom: 1,
  phylum: 2,
  class: 3,
  order: 4,
  family: 5,
  genus: 6,
  species: 7
}

/** A taxonomy-rank NAME → its compacted-ladder depth, or `undefined` when [name] is not a known rank. */
export function rankNameToDepth(name: string | null | undefined): number | undefined {
  if (name == null) return undefined
  return RANK_NAME_TO_DEPTH[name.trim().toLowerCase()]
}

/**
 * Single-bucket aggregation by the taxon at a FIXED compacted-ladder [depth] (a "rank", relative to
 * root). Every value whose lineage does not resolve a real taxon at that depth — the lineage is
 * shorter, or the entry is a NaN-like placeholder ({@link NON_TAXON_TOKENS}) — folds into one
 * [unresolvedLabel] bucket rather than being dropped or producing a "nan" bar.
 *
 * Used by the Ranked Bar sink for both "direct children of the zoom entry" (`depth === selectedPath.length`)
 * and an explicit `rankLevel` override.
 */
export function aggregateTaxaAtDepthSingleBucket(opts: {
  filteredBuckets: FilteredBucketValues[]
  bucketKey: string
  depth: number
  unresolvedLabel: string
}): TaxaAggregationResult {
  const { filteredBuckets, bucketKey, depth, unresolvedLabel } = opts

  const bucket = filteredBuckets.find((x) => x.bucketKey === bucketKey) ?? {
    bucketKey,
    bucketLabel: bucketKey,
    values: []
    /* no sampleCount: nothing was found, so the count is unknown rather than zero */
  }

  const agg: Record<string, number> = {}
  for (const v of bucket.values) {
    const at = v.taxa[depth]
    const key = isMeaningfulTaxon(at) ? at! : unresolvedLabel
    agg[key] = (agg[key] ?? 0) + (v.magnitude ?? 0)
  }

  const taxa = Object.keys(agg)
  return {
    taxa,
    rows: [{ bucketKey: bucket.bucketKey, bucketLabel: bucket.bucketLabel, sampleCount: bucket.sampleCount, data: agg }]
  }
}

/** Suffix for the "lineage stops at the zoom entry" bucket, e.g. `Diptera (unresolved)`. */
export const unresolvedChildLabel = (entryLabel: string): string => `${entryLabel} (unresolved)`

/**
 * The Ranked Bar aggregation, mode-dispatched (the [filteredBuckets] have ALREADY had the
 * `selectedPath` prefix + grade filter applied by the shared cube filter):
 *
 * 1. `rankLevel` set (a rank NAME) → rank ALL values at exactly that named rank (its compacted
 *    depth). Wins over the entry/leaf logic. Within the zoom subtree, since the prefix filter runs
 *    upstream. Unresolved-at-that-rank reads fold into one {@link UNRESOLVED_LABEL} bucket.
 * 2. else `selectedPath` non-empty (zoomed into a `taxonEntry`) → rank the entry's DIRECT CHILDREN
 *    one rank down the compacted ladder (`depth === selectedPath.length`); lineages that terminate
 *    AT the entry fold into a single `"<entry> (unresolved)"` bucket.
 * 3. else (root, no entry) → the legacy LEAF ranking ({@link aggregateTaxaLeafSingleBucket}). This
 *    is deliberate: root-level children collapse to a single useless "Eukaryota" bar, so at root we
 *    rank the deepest-resolved organisms instead.
 *
 * Feed the result to `rankTopTaxa` (sort desc + top-N) exactly as before.
 */
export function aggregateRankedBar(opts: {
  filteredBuckets: FilteredBucketValues[]
  bucketKey: string
  selectedPath: string[]
  rankLevel?: string
}): TaxaAggregationResult {
  const { filteredBuckets, bucketKey, selectedPath, rankLevel } = opts

  const rankDepth = rankNameToDepth(rankLevel)
  if (rankDepth !== undefined) {
    return aggregateTaxaAtDepthSingleBucket({ filteredBuckets, bucketKey, depth: rankDepth, unresolvedLabel: UNRESOLVED_LABEL })
  }

  if (selectedPath.length > 0) {
    const entryLabel = deepestMeaningfulTaxon(selectedPath)
    return aggregateTaxaAtDepthSingleBucket({
      filteredBuckets,
      bucketKey,
      depth: selectedPath.length,
      unresolvedLabel: unresolvedChildLabel(entryLabel)
    })
  }

  return aggregateTaxaLeafSingleBucket({ filteredBuckets, bucketKey })
}
